import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Portal,
  Typography,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useClientExtensions } from './ClientExtensionContext';


export const TUTORIAL_EXTENSION_ENDPOINT = 'tutorial.show_scene';
export const TUTORIAL_BUILDER_MESSAGE_SOURCE = 'dopatize.tutorial-builder';
export const TUTORIAL_PREVIEW_MESSAGE_SOURCE = 'dopatize.tutorial-preview';
const VIEWPORT_MARGIN = 12;
const DEFAULT_TYPING = {
  chars_per_second: 16,
  period_pause_multiplier: 4,
  play_every_n_letters: 1,
  sound_on_whitespace: false,
  sound_path: '/dopi_voice.wav',
  sound_gain: 1,
  blip_ms: 60,
  base_pitch_hz: 300,
  pitch_jitter_hz: 80,
};
const DEFAULT_SCENE = {
  focus: { spotlight: false, zoom: 1.08 },
  layout: {
    responsive: true,
    reference_viewport: { width_px: 900, height_px: 600 },
    minimum_scale: 0.35,
    maximum_scale: 1,
  },
  sprite: {
    anchor: 'viewport',
    placement: 'left',
    viewport: { x_percent: 50, y_percent: 50 },
    offset_px: { x: 0, y: 0 },
    rotation_deg: 0,
    facing: 'right',
    width_px: 96,
  },
  dialogue: {
    anchor: 'sprite',
    placement: 'right',
    viewport: { x_percent: 50, y_percent: 50 },
    offset_px: { x: 0, y: 0 },
    rotation_deg: 0,
    text_align: 'left',
    width_px: 558,
    height_px: 230,
    font_size_px: 13,
  },
  speech_tail: {
    tip_offset_px: { x: 0, y: 0 },
    base_offset_px: { x: 0, y: 0 },
    base_width_px: 52,
  },
};
const TARGET_PATTERN = /^[A-Za-z0-9_.:-]{1,120}$/;
const INVALID_INTERNAL_ROUTE = Symbol('invalid-internal-route');

export const suppressTutorialPickerActivation = (event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
};

const objectValue = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const normalizeInternalRoute = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return INVALID_INTERNAL_ROUTE;
  const route = value.trim();
  if (!route) return null;
  try {
    const parsed = new URL(route, window.location.origin);
    if (!route.startsWith('/') || route.startsWith('//') || parsed.origin !== window.location.origin) {
      return INVALID_INTERNAL_ROUTE;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_error) {
    return INVALID_INTERNAL_ROUTE;
  }
};

const normalizeTutorialPart = (part) => {
  const payload = objectValue(part);
  const message = typeof payload.message === 'string' ? payload.message.trim() : '';
  const target = payload.target ?? null;
  const advanceOnElementId = payload.advance_on_element_id ?? null;
  const typing = objectValue(payload.typing);
  const navigateTo = normalizeInternalRoute(payload.navigate_to);
  const soundPath = normalizeInternalRoute(typing.sound_path);
  if (
    !message ||
    (target !== null && (typeof target !== 'string' || !TARGET_PATTERN.test(target))) ||
    (advanceOnElementId !== null && (
      typeof advanceOnElementId !== 'string' || !TARGET_PATTERN.test(advanceOnElementId)
    )) ||
    navigateTo === INVALID_INTERNAL_ROUTE ||
    soundPath === INVALID_INTERNAL_ROUTE
  ) return null;

  const focus = objectValue(payload.focus);
  const layout = objectValue(payload.layout);
  const referenceViewport = objectValue(layout.reference_viewport);
  const sprite = objectValue(payload.sprite);
  const spriteViewport = objectValue(sprite.viewport);
  const spriteOffset = objectValue(sprite.offset_px);
  const dialogue = objectValue(payload.dialogue);
  const dialogueViewport = objectValue(dialogue.viewport);
  const dialogueOffset = objectValue(dialogue.offset_px);
  const speechTail = objectValue(payload.speech_tail);

  return {
      ...payload,
      message: message.slice(0, 4000),
      navigate_to: navigateTo,
      target,
      advance_on_element_id: advanceOnElementId,
      focus: { ...DEFAULT_SCENE.focus, ...focus },
      layout: {
        ...DEFAULT_SCENE.layout,
        ...layout,
        reference_viewport: {
          ...DEFAULT_SCENE.layout.reference_viewport,
          ...referenceViewport,
        },
      },
      sprite: {
        ...DEFAULT_SCENE.sprite,
        ...sprite,
        anchor: sprite.anchor || (target ? 'target' : 'viewport'),
        viewport: { ...DEFAULT_SCENE.sprite.viewport, ...spriteViewport },
        offset_px: { ...DEFAULT_SCENE.sprite.offset_px, ...spriteOffset },
      },
      dialogue: {
        ...DEFAULT_SCENE.dialogue,
        ...dialogue,
        viewport: { ...DEFAULT_SCENE.dialogue.viewport, ...dialogueViewport },
        offset_px: { ...DEFAULT_SCENE.dialogue.offset_px, ...dialogueOffset },
      },
      speech_tail: {
        ...DEFAULT_SCENE.speech_tail,
        ...speechTail,
        tip_offset_px: {
          ...DEFAULT_SCENE.speech_tail.tip_offset_px,
          ...objectValue(speechTail.tip_offset_px),
        },
        base_offset_px: {
          ...DEFAULT_SCENE.speech_tail.base_offset_px,
          ...objectValue(speechTail.base_offset_px),
        },
      },
      typing: {
        ...DEFAULT_TYPING,
        ...typing,
        sound_path: soundPath || DEFAULT_TYPING.sound_path,
      },
  };
};

export const normalizeTutorialDelivery = (delivery) => {
  const payload = objectValue(delivery?.payload);
  let sourceParts;
  if (payload.version === 1) sourceParts = [payload];
  else if (payload.version === 2 && Array.isArray(payload.parts) && payload.parts.length) {
    sourceParts = payload.parts;
  } else return null;

  const parts = sourceParts.map(normalizeTutorialPart);
  if (parts.some((part) => !part)) return null;
  return {
    ...delivery,
    id: delivery?.id || delivery?.trigger_id || delivery?.event_id,
    payload: {
      version: 2,
      parts,
      next_action: payload.next_action === 'return_to_task' ? 'return_to_task' : 'dismiss',
    },
  };
};

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

export const tutorialResponsiveScale = (layout, viewport) => {
  if (layout?.responsive === false) return 1;
  const referenceWidth = Number(layout?.reference_viewport?.width_px) || 900;
  const referenceHeight = Number(layout?.reference_viewport?.height_px) || 600;
  const minimumScale = Number(layout?.minimum_scale) || 0.35;
  const maximumScale = Number(layout?.maximum_scale) || 1;
  return clamp(
    Math.min(viewport.width / referenceWidth, viewport.height / referenceHeight),
    minimumScale,
    Math.max(minimumScale, maximumScale),
  );
};

export const tutorialDialogueMetrics = (dialogue, responsiveScale, viewport, layout = {}) => {
  const availableWidth = Math.max(120, viewport.width - VIEWPORT_MARGIN * 2);
  const availableHeight = Math.max(100, viewport.height - VIEWPORT_MARGIN * 2);
  const compact = viewport.width <= 600;
  const minimumWidth = Math.min(compact ? 180 : 220, availableWidth);
  const minimumHeight = Math.min(compact ? 105 : 140, availableHeight);
  const referenceWidth = Number(layout?.reference_viewport?.width_px) || 900;
  const referenceHeight = Number(layout?.reference_viewport?.height_px) || 600;
  const referenceAspectRatio = referenceWidth / referenceHeight;
  const viewportAspectRatio = viewport.width / Math.max(1, viewport.height);
  const aspectAdjustment = Math.sqrt(clamp(
    viewportAspectRatio / referenceAspectRatio,
    0.25,
    1,
  ));
  const widthScale = responsiveScale * aspectAdjustment;
  const heightScale = responsiveScale / aspectAdjustment;
  const fontSize = compact
    ? 8
    : clamp((dialogue?.font_size_px ?? 13) * responsiveScale, 8, 32);
  return {
    width: clamp((dialogue?.width_px ?? 558) * widthScale, minimumWidth, availableWidth),
    height: clamp((dialogue?.height_px ?? 230) * heightScale, minimumHeight, availableHeight),
    fontSize,
    padding: compact
      ? clamp(24 * responsiveScale * 0.6, 6, 10)
      : clamp(24 * responsiveScale, 10, 24),
    borderWidth: compact
      ? clamp(5 * responsiveScale * 0.65, 2, 3)
      : clamp(5 * responsiveScale, 3, 5),
    borderRadius: compact
      ? clamp(38 * responsiveScale * 0.65, 12, 18)
      : clamp(38 * responsiveScale, 18, 38),
    buttonFontSize: compact ? 8 : clamp(fontSize * 0.9, 8, 13),
    buttonHeight: compact ? 24 : Math.max(28, 42 * responsiveScale),
    buttonPaddingY: compact ? 2 : Math.max(4, 8 * responsiveScale),
    iconSize: compact
      ? clamp(24 * responsiveScale * 0.65, 13, 16)
      : clamp(24 * responsiveScale, 16, 24),
    letterSpacing: compact ? 0.5 : 1,
  };
};

const scaledTutorialPosition = (position, scale) => ({
  ...position,
  offset_px: {
    x: (position.offset_px?.x || 0) * scale,
    y: (position.offset_px?.y || 0) * scale,
  },
});

export const tutorialPitchRatio = (basePitch, pitchJitter, randomValue = Math.random()) => {
  const safeBase = Math.max(1, Number(basePitch) || DEFAULT_TYPING.base_pitch_hz);
  const safeJitter = Math.max(0, Number(pitchJitter) || 0);
  const pitch = safeBase + ((randomValue * 2) - 1) * safeJitter;
  return clamp(pitch / safeBase, 0.25, 4);
};

export const tutorialCharacterDelay = (previousCharacter, settings = DEFAULT_TYPING) => {
  const baseDelay = 1000 / Math.max(1, Number(settings.chars_per_second) || DEFAULT_TYPING.chars_per_second);
  return /[.!?]/.test(previousCharacter || '')
    ? baseDelay * (Number(settings.period_pause_multiplier) || DEFAULT_TYPING.period_pause_multiplier)
    : baseDelay;
};

const useReducedMotion = () => {
  const getPreference = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const [reducedMotion, setReducedMotion] = useState(getPreference);
  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return undefined;
    const update = () => setReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);
  return reducedMotion;
};

const useTutorialVoice = (soundPath) => {
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const loadedPathRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioContextRef.current = new AudioContextClass();
    return audioContextRef.current;
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      const audioContext = getAudioContext();
      if (audioContext?.state === 'suspended') audioContext.resume().catch(() => {});
    };
    window.addEventListener('pointerdown', unlockAudio, true);
    return () => window.removeEventListener('pointerdown', unlockAudio, true);
  }, [getAudioContext]);

  useEffect(() => {
    audioBufferRef.current = null;
    loadedPathRef.current = null;
    if (!soundPath) return undefined;

    const audioContext = getAudioContext();
    if (!audioContext || typeof window.fetch !== 'function') return undefined;
    let cancelled = false;
    window.fetch(soundPath)
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load tutorial voice (${response.status})`);
        return response.arrayBuffer();
      })
      .then((data) => audioContext.decodeAudioData(data))
      .then((buffer) => {
        if (!cancelled) {
          audioBufferRef.current = buffer;
          loadedPathRef.current = soundPath;
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [getAudioContext, soundPath]);

  return useCallback((settings) => {
    const audioContext = audioContextRef.current;
    const audioBuffer = audioBufferRef.current;
    if (!audioContext || !audioBuffer || loadedPathRef.current !== settings.sound_path) return;

    if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
    const duration = Math.max(0.02, Number(settings.blip_ms) / 1000);
    const fadeDuration = Math.min(0.008, duration / 3);
    const effectiveGain = clamp(Number(settings.sound_gain) || 0, 0, 2) * 0.25;
    const now = audioContext.currentTime;
    const source = audioContext.createBufferSource();
    const gain = audioContext.createGain();
    source.buffer = audioBuffer;
    source.playbackRate.setValueAtTime(
      tutorialPitchRatio(settings.base_pitch_hz, settings.pitch_jitter_hz),
      now,
    );
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(effectiveGain, now + fadeDuration);
    gain.gain.setValueAtTime(effectiveGain, now + duration - fadeDuration);
    gain.gain.linearRampToValueAtTime(0, now + duration);
    source.connect(gain);
    gain.connect(audioContext.destination);
    source.start(now, 0);
    source.stop(now + duration);
  }, []);
};

const useWindowSize = () => {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const update = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
};

const useScenePageReady = (actionId, navigateTo, location) => {
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    setPageReady(false);
    if (!actionId) return undefined;

    if (navigateTo) {
      const destination = new URL(navigateTo, window.location.origin);
      if (
        destination.pathname !== location.pathname ||
        destination.search !== location.search ||
        destination.hash !== location.hash
      ) {
        return undefined;
      }
    }

    let firstFrame;
    let secondFrame;
    let settleTimer;
    let pageShellTimer;
    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 16));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const waitForPaint = () => {
      firstFrame = requestFrame(() => {
        secondFrame = requestFrame(() => {
          settleTimer = window.setTimeout(() => setPageReady(true), 100);
        });
      });
    };
    const waitForPageShell = () => {
      if (document.querySelector('[data-app-page-loading="true"]')) {
        pageShellTimer = window.setTimeout(waitForPageShell, 50);
        return;
      }
      waitForPaint();
    };

    if (document.readyState === 'loading') window.addEventListener('load', waitForPageShell, { once: true });
    else waitForPageShell();

    return () => {
      window.removeEventListener('load', waitForPageShell);
      cancelFrame(firstFrame);
      cancelFrame(secondFrame);
      window.clearTimeout(settleTimer);
      window.clearTimeout(pageShellTimer);
    };
  }, [actionId, location.hash, location.pathname, location.search, navigateTo]);

  return pageReady;
};

const viewportPoint = (position, viewport) => ({
  x: viewport.width * ((position.viewport?.x_percent ?? 50) / 100),
  y: viewport.height * ((position.viewport?.y_percent ?? 50) / 100),
});

const placedPoint = (position, anchorRect, itemSize, viewport) => {
  const offset = position.offset_px || { x: 0, y: 0 };
  if (!anchorRect || position.anchor === 'viewport') {
    const point = viewportPoint(position, viewport);
    return {
      x: clamp(point.x + offset.x, VIEWPORT_MARGIN + itemSize.width / 2, viewport.width - VIEWPORT_MARGIN - itemSize.width / 2),
      y: clamp(point.y + offset.y, VIEWPORT_MARGIN + itemSize.height / 2, viewport.height - VIEWPORT_MARGIN - itemSize.height / 2),
    };
  }

  const gap = 16;
  let x = anchorRect.left + anchorRect.width / 2;
  let y = anchorRect.top + anchorRect.height / 2;
  if (position.placement === 'left') x = anchorRect.left - gap - itemSize.width / 2;
  if (position.placement === 'right') x = anchorRect.right + gap + itemSize.width / 2;
  if (position.placement === 'top') y = anchorRect.top - gap - itemSize.height / 2;
  if (position.placement === 'bottom') y = anchorRect.bottom + gap + itemSize.height / 2;

  return {
    x: clamp(x + offset.x, VIEWPORT_MARGIN + itemSize.width / 2, viewport.width - VIEWPORT_MARGIN - itemSize.width / 2),
    y: clamp(y + offset.y, VIEWPORT_MARGIN + itemSize.height / 2, viewport.height - VIEWPORT_MARGIN - itemSize.height / 2),
  };
};

const rotatePoint = (point, center, rotationDegrees) => {
  if (!rotationDegrees) return point;
  const radians = rotationDegrees * (Math.PI / 180);
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const x = point.x - center.x;
  const y = point.y - center.y;
  return {
    x: center.x + x * cosine - y * sine,
    y: center.y + x * sine + y * cosine,
  };
};

const getSpeechTailGeometry = (mouthPoint, dialogueRect, dialogueRotation, speechTail) => {
  const centerX = dialogueRect.left + dialogueRect.width / 2;
  const centerY = dialogueRect.top + dialogueRect.height / 2;
  const center = { x: centerX, y: centerY };
  const localMouth = rotatePoint(mouthPoint, center, -dialogueRotation);
  const horizontalDistance = Math.abs(localMouth.x - centerX) / dialogueRect.width;
  const verticalDistance = Math.abs(localMouth.y - centerY) / dialogueRect.height;
  let baseStart;
  let baseEnd;
  const halfBaseWidth = (speechTail.base_width_px ?? 52) / 2;

  if (horizontalDistance > verticalDistance) {
    const x = localMouth.x < centerX ? dialogueRect.left + 3 : dialogueRect.right - 3;
    const y = clamp(localMouth.y, dialogueRect.top + 28, dialogueRect.bottom - 28);
    baseStart = { x, y: y - halfBaseWidth };
    baseEnd = { x, y: y + halfBaseWidth };
  } else {
    const y = localMouth.y < centerY ? dialogueRect.top + 3 : dialogueRect.bottom - 3;
    const x = clamp(localMouth.x, dialogueRect.left + 34, dialogueRect.right - 34);
    baseStart = { x: x - halfBaseWidth, y };
    baseEnd = { x: x + halfBaseWidth, y };
  }
  const baseOffset = speechTail.base_offset_px || { x: 0, y: 0 };
  const rotatedStart = rotatePoint(baseStart, center, dialogueRotation);
  const rotatedEnd = rotatePoint(baseEnd, center, dialogueRotation);
  const offsetStart = { x: rotatedStart.x + baseOffset.x, y: rotatedStart.y + baseOffset.y };
  const offsetEnd = { x: rotatedEnd.x + baseOffset.x, y: rotatedEnd.y + baseOffset.y };
  return {
    points: `${mouthPoint.x},${mouthPoint.y} ${offsetStart.x},${offsetStart.y} ${offsetEnd.x},${offsetEnd.y}`,
    baseCenter: {
      x: (offsetStart.x + offsetEnd.x) / 2,
      y: (offsetStart.y + offsetEnd.y) / 2,
    },
  };
};

const AuthorHandle = ({ label, symbol, point, onPointerDown, color = '#00E5FF' }) => (
  <Box
    component="button"
    type="button"
    aria-label={label}
    title={label}
    onPointerDown={onPointerDown}
    sx={{
      position: 'fixed',
      left: point.x,
      top: point.y,
      zIndex: 1650,
      width: 28,
      height: 28,
      minWidth: 28,
      p: 0,
      m: 0,
      transform: 'translate(-50%, -50%)',
      border: `3px solid ${color}`,
      borderRadius: '50%',
      bgcolor: '#090D0E',
      color,
      fontFamily: 'monospace',
      fontSize: '14px',
      lineHeight: 1,
      cursor: 'grab',
      boxShadow: `0 0 12px ${color}`,
      touchAction: 'none',
      '&:hover': { bgcolor: '#18201C', color },
      '&:active': { cursor: 'grabbing' },
    }}
  >
    {symbol}
  </Box>
);

export const TutorialOverlay = ({
  current,
  minimized,
  onMinimize,
  onNext,
  onRestore,
  onAuthorChange,
}) => {
  const [targetRect, setTargetRect] = useState(null);
  const [visibleCharacterCount, setVisibleCharacterCount] = useState(0);
  const spokenCharacterCountRef = useRef(0);
  const location = useLocation();
  const viewport = useWindowSize();
  const reducedMotion = useReducedMotion();
  const action = current?.action;
  const partIndex = current?.partIndex || 0;
  const payload = current?.part || action?.payload?.parts?.[partIndex] || action?.payload;
  const sceneId = action?.id ? `${action.id}:${partIndex}` : null;
  const authorMode = Boolean(current?.isAuthorPreview && onAuthorChange);
  const dragCleanupRef = useRef(null);
  const pageReady = useScenePageReady(sceneId, payload?.navigate_to, location);
  const message = payload?.message || '';
  const targetId = payload?.target;
  const advancesOnElementClick = Boolean(payload?.advance_on_element_id);
  const focusZoom = payload?.focus?.zoom ?? 1.08;
  const typing = useMemo(() => ({ ...DEFAULT_TYPING, ...(payload?.typing || {}) }), [payload?.typing]);
  const playVoice = useTutorialVoice(typing.sound_path);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  const startPointerDrag = useCallback((event, handleMove) => {
    if (!authorMode) return;
    event.preventDefault();
    event.stopPropagation();
    dragCleanupRef.current?.();
    const startX = Number(event.clientX) || 0;
    const startY = Number(event.clientY) || 0;
    const move = (moveEvent) => handleMove(
      (Number(moveEvent.clientX) || 0) - startX,
      (Number(moveEvent.clientY) || 0) - startY,
    );
    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
      dragCleanupRef.current = null;
    };
    dragCleanupRef.current = cleanup;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
  }, [authorMode]);

  useEffect(() => {
    setVisibleCharacterCount(reducedMotion ? message.length : 0);
    spokenCharacterCountRef.current = 0;
  }, [message, reducedMotion, sceneId]);

  useEffect(() => {
    if (!pageReady || minimized || reducedMotion || visibleCharacterCount >= message.length) return undefined;
    const previousCharacter = visibleCharacterCount > 0 ? message[visibleCharacterCount - 1] : '';
    const delay = visibleCharacterCount === 0 ? 650 : tutorialCharacterDelay(previousCharacter, typing);
    const timerId = window.setTimeout(() => {
      const character = message[visibleCharacterCount];
      const eligibleForSound = typing.sound_on_whitespace || !/\s/.test(character);
      if (eligibleForSound) {
        spokenCharacterCountRef.current += 1;
        if (spokenCharacterCountRef.current % Math.max(1, typing.play_every_n_letters) === 0) {
          playVoice(typing);
        }
      }
      setVisibleCharacterCount((count) => Math.min(message.length, count + 1));
    }, delay);
    return () => window.clearTimeout(timerId);
  }, [message, minimized, pageReady, playVoice, reducedMotion, typing, visibleCharacterCount]);

  const isTyping = visibleCharacterCount < message.length;
  const handleAdvance = useCallback(() => {
    if (isTyping) {
      setVisibleCharacterCount(message.length);
      return;
    }
    onNext();
  }, [isTyping, message.length, onNext]);

  useEffect(() => {
    if (!payload || minimized || advancesOnElementClick) return undefined;
    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName;
      if (event.key === 'Enter' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) {
        event.preventDefault();
        handleAdvance();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [advancesOnElementClick, handleAdvance, minimized, payload]);

  useLayoutEffect(() => {
    if (!pageReady || !targetId) {
      setTargetRect(null);
      return undefined;
    }

    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
      setTargetRect(null);
      return undefined;
    }
    let previousStyles;
    let observer;

    const refreshRect = () => {
      if (!targetElement || !document.body.contains(targetElement)) return;
      setTargetRect(targetElement.getBoundingClientRect());
    };

    targetElement.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' });
    previousStyles = {
      transform: targetElement.style.transform,
      transformOrigin: targetElement.style.transformOrigin,
      transition: targetElement.style.transition,
    };
    targetElement.style.transform = `${previousStyles.transform || ''} scale(${focusZoom})`.trim();
    targetElement.style.transformOrigin = 'center';
    targetElement.style.transition = 'transform 180ms ease';
    refreshRect();
    window.addEventListener('scroll', refreshRect, true);
    window.addEventListener('resize', refreshRect);
    if (window.ResizeObserver) {
      observer = new ResizeObserver(refreshRect);
      observer.observe(targetElement);
    }

    return () => {
      window.removeEventListener('scroll', refreshRect, true);
      window.removeEventListener('resize', refreshRect);
      observer?.disconnect();
      if (targetElement && previousStyles) {
        targetElement.style.transform = previousStyles.transform;
        targetElement.style.transformOrigin = previousStyles.transformOrigin;
        targetElement.style.transition = previousStyles.transition;
      }
    };
  }, [focusZoom, location.key, pageReady, targetId]);

  if (!payload || !pageReady) return null;

  const responsiveScale = tutorialResponsiveScale(payload.layout, viewport);
  const authoredSpriteWidth = payload.sprite?.width_px ?? 96;
  const spriteWidth = Math.max(36, authoredSpriteWidth * responsiveScale);
  const spriteRotation = payload.sprite?.rotation_deg ?? 0;
  const dialogueRotation = payload.dialogue?.rotation_deg ?? 0;
  const spritePosition = scaledTutorialPosition(payload.sprite, responsiveScale);
  const dialoguePosition = scaledTutorialPosition(payload.dialogue, responsiveScale);
  const speechTail = {
    tip_offset_px: {
      x: (payload.speech_tail?.tip_offset_px?.x || 0) * responsiveScale,
      y: (payload.speech_tail?.tip_offset_px?.y || 0) * responsiveScale,
    },
    base_offset_px: {
      x: (payload.speech_tail?.base_offset_px?.x || 0) * responsiveScale,
      y: (payload.speech_tail?.base_offset_px?.y || 0) * responsiveScale,
    },
    base_width_px: (payload.speech_tail?.base_width_px ?? 52) * responsiveScale,
  };
  const spriteSize = { width: spriteWidth, height: spriteWidth };
  const spritePoint = placedPoint(spritePosition, targetRect, spriteSize, viewport);
  const spriteRect = {
    left: spritePoint.x - spriteSize.width / 2,
    right: spritePoint.x + spriteSize.width / 2,
    top: spritePoint.y - spriteSize.height / 2,
    bottom: spritePoint.y + spriteSize.height / 2,
    width: spriteSize.width,
    height: spriteSize.height,
  };
  const dialogueMetrics = tutorialDialogueMetrics(
    payload.dialogue,
    responsiveScale,
    viewport,
    payload.layout,
  );
  const dialogueWidth = dialogueMetrics.width;
  const dialogueHeight = dialogueMetrics.height;
  const dialogueSize = { width: dialogueWidth, height: dialogueHeight };
  const dialogueAnchor = payload.dialogue?.anchor === 'target' ? targetRect : spriteRect;
  const dialoguePoint = placedPoint(dialoguePosition, dialogueAnchor, dialogueSize, viewport);
  const dialogueRect = {
    left: dialoguePoint.x - dialogueWidth / 2,
    right: dialoguePoint.x + dialogueWidth / 2,
    top: dialoguePoint.y - dialogueHeight / 2,
    bottom: dialoguePoint.y + dialogueHeight / 2,
    width: dialogueWidth,
    height: dialogueHeight,
  };
  const automaticMouthPoint = rotatePoint({
    x: spritePoint.x + (payload.sprite?.facing === 'left' ? -0.24 : 0.24) * spriteWidth,
    y: spritePoint.y + spriteWidth * 0.04,
  }, spritePoint, spriteRotation);
  const mouthPoint = {
    x: automaticMouthPoint.x + speechTail.tip_offset_px.x,
    y: automaticMouthPoint.y + speechTail.tip_offset_px.y,
  };
  const tailGeometry = getSpeechTailGeometry(mouthPoint, dialogueRect, dialogueRotation, speechTail);
  const tailPoints = tailGeometry.points;
  const transformOrigin = `${clamp(mouthPoint.x - dialogueRect.left, 0, dialogueWidth)}px ${clamp(mouthPoint.y - dialogueRect.top, 0, dialogueHeight)}px`;
  const spriteResizePoint = rotatePoint(
    { x: spriteRect.right, y: spriteRect.bottom },
    spritePoint,
    spriteRotation,
  );
  const dialogueDragPoint = rotatePoint(
    { x: dialogueRect.left + 24, y: dialogueRect.top + 24 },
    dialoguePoint,
    dialogueRotation,
  );

  const dragPosition = (event, section) => {
    const initialOffset = payload[section]?.offset_px || { x: 0, y: 0 };
    startPointerDrag(event, (deltaX, deltaY) => onAuthorChange({
      ...payload,
      [section]: {
        ...payload[section],
        offset_px: {
          x: Math.round(clamp(initialOffset.x + deltaX / responsiveScale, -2000, 2000)),
          y: Math.round(clamp(initialOffset.y + deltaY / responsiveScale, -2000, 2000)),
        },
      },
    }));
  };

  const resizeSprite = (event) => {
    const initialWidth = authoredSpriteWidth;
    startPointerDrag(event, (deltaX, deltaY) => onAuthorChange({
      ...payload,
      sprite: {
        ...payload.sprite,
        width_px: Math.round(clamp(
          initialWidth + (deltaX + deltaY) / (2 * responsiveScale),
          48,
          240,
        )),
      },
    }));
  };

  const dragTailPoint = (event, key) => {
    const initialOffset = payload.speech_tail?.[key] || { x: 0, y: 0 };
    startPointerDrag(event, (deltaX, deltaY) => onAuthorChange({
      ...payload,
      speech_tail: {
        ...payload.speech_tail,
        [key]: {
          x: Math.round(clamp(initialOffset.x + deltaX / responsiveScale, -2000, 2000)),
          y: Math.round(clamp(initialOffset.y + deltaY / responsiveScale, -2000, 2000)),
        },
      },
    }));
  };

  return (
    <Portal>
      {targetRect && payload.focus?.spotlight && (
        <Box
          data-testid="tutorial-spotlight"
          sx={{
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 1490,
            left: targetRect.left - 8,
            top: targetRect.top - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            border: '3px solid',
            borderColor: 'primary.main',
            borderRadius: 3,
            boxShadow: '0 0 0 9999px rgba(9,13,14,.62), 0 0 28px rgba(0,229,255,.8)',
          }}
        />
      )}
      <Box
        component="img"
        src="/dope_logo_512.png"
        alt="Dopatize tutorial guide"
        aria-label={minimized ? 'Restore tutorial' : undefined}
        role={minimized ? 'button' : undefined}
        tabIndex={minimized ? 0 : undefined}
        draggable={false}
        data-tutorial-interactive="sprite"
        onPointerDown={authorMode ? (event) => dragPosition(event, 'sprite') : undefined}
        onClick={minimized ? () => {
          setVisibleCharacterCount(message.length);
          onRestore();
        } : undefined}
        onKeyDown={minimized ? (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setVisibleCharacterCount(message.length);
          onRestore();
        } : undefined}
        sx={{
          position: 'fixed',
          pointerEvents: authorMode || minimized ? 'auto' : 'none',
          zIndex: 1510,
          left: spritePoint.x,
          top: spritePoint.y,
          width: spriteWidth,
          height: spriteWidth,
          objectFit: 'contain',
          cursor: authorMode ? 'grab' : minimized ? 'pointer' : 'default',
          touchAction: 'none',
          transform: `translate(-50%, -50%) rotate(${spriteRotation}deg) scaleX(${payload.sprite?.facing === 'left' ? -1 : 1})`,
          filter: 'drop-shadow(0 0 14px rgba(0,229,255,.7))',
          '@keyframes tutorialSpriteEnter': {
            from: { opacity: 0, marginTop: 12, filter: 'drop-shadow(0 0 0 rgba(0,229,255,0))' },
            to: { opacity: 1, marginTop: 0, filter: 'drop-shadow(0 0 14px rgba(0,229,255,.7))' },
          },
          animation: 'tutorialSpriteEnter 220ms ease-out both',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      {!minimized && <Box
          component="svg"
          data-testid="tutorial-speech-tail"
          aria-hidden="true"
          viewBox={`0 0 ${viewport.width} ${viewport.height}`}
          preserveAspectRatio="none"
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1515,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="polygon"
            points={tailPoints}
            fill="#090D0E"
            stroke="#39FF14"
            strokeWidth="5"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            sx={{
              transformBox: 'view-box',
              transformOrigin: `${mouthPoint.x}px ${mouthPoint.y}px`,
              '@keyframes tutorialTailGrow': {
                from: { opacity: 0, transform: 'scale(0)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
              animation: 'tutorialTailGrow 280ms cubic-bezier(.2,.8,.25,1) 100ms both',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        </Box>}
      {!minimized && <Box
        role="dialog"
        aria-modal="false"
        aria-label="Tutorial message"
        data-tutorial-interactive="dialogue"
        style={{ WebkitTextSizeAdjust: 'none', textSizeAdjust: 'none' }}
        sx={{
          position: 'fixed',
          zIndex: 1520,
          left: dialoguePoint.x,
          top: dialoguePoint.y,
          width: dialogueWidth,
          height: dialogueHeight,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          p: `${dialogueMetrics.padding}px`,
          bgcolor: '#090D0E',
          border: `${dialogueMetrics.borderWidth}px solid #39FF14`,
          borderRadius: `${dialogueMetrics.borderRadius}px`,
          boxShadow: `0 ${Math.max(4, 7 * responsiveScale)}px 0 #137c08, 0 0 ${Math.max(14, 26 * responsiveScale)}px rgba(57,255,20,.48)`,
          pointerEvents: 'auto',
          transformOrigin,
          '@keyframes tutorialDialogueOpen': {
            '0%': { opacity: 0, transform: `translate(-50%, -50%) rotate(${dialogueRotation}deg) scale(.04)` },
            '72%': { opacity: 1, transform: `translate(-50%, -50%) rotate(${dialogueRotation}deg) scale(1.035)` },
            '100%': { opacity: 1, transform: `translate(-50%, -50%) rotate(${dialogueRotation}deg) scale(1)` },
          },
          animation: 'tutorialDialogueOpen 420ms cubic-bezier(.18,.82,.2,1) 180ms both',
          transform: `translate(-50%, -50%) rotate(${dialogueRotation}deg)`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Box sx={{ position: 'absolute', top: Math.max(3, 8 * responsiveScale), right: Math.max(4, 10 * responsiveScale) }}>
          <IconButton
            aria-label="Minimize tutorial"
            onClick={onMinimize}
            size="small"
            sx={{
              color: '#39FF14',
              width: dialogueMetrics.iconSize + 8,
              height: dialogueMetrics.iconSize + 8,
              '& .MuiSvgIcon-root': { fontSize: dialogueMetrics.iconSize },
            }}
          >
            <RemoveIcon />
          </IconButton>
        </Box>
        <Typography
          aria-live="polite"
          aria-label={message}
          style={{
            fontSize: `${dialogueMetrics.fontSize}px`,
            letterSpacing: `${dialogueMetrics.letterSpacing}px`,
            WebkitTextSizeAdjust: 'none',
            textSizeAdjust: 'none',
          }}
          sx={{
            color: '#39FF14',
            textAlign: payload.dialogue?.text_align ?? 'left',
            fontFamily: "'Press Start 2P', monospace",
            lineHeight: 1.25,
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
            flex: 1,
            pr: `${dialogueMetrics.iconSize + 6}px`,
            mb: `${Math.max(5, 12 * responsiveScale)}px`,
          }}
        >
          {message.slice(0, visibleCharacterCount)}
        </Typography>
        {!advancesOnElementClick && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleAdvance}
            aria-label={isTyping ? 'Show full tutorial message' : 'Next tutorial scene'}
            style={{
              fontSize: `${dialogueMetrics.buttonFontSize}px`,
              WebkitTextSizeAdjust: 'none',
              textSizeAdjust: 'none',
            }}
            sx={{
              flexShrink: 0,
              minHeight: dialogueMetrics.buttonHeight,
              py: `${dialogueMetrics.buttonPaddingY}px`,
              color: '#090D0E',
              bgcolor: '#39FF14',
              boxShadow: '0 5px 0 #137c08',
              '&:hover': { bgcolor: '#58ff3b', transform: 'translate(-1px, -1px)' },
              '&:active': { boxShadow: '0 2px 0 #137c08', transform: 'translateY(3px)' },
            }}
          >
            {isTyping ? 'Show text' : 'Next'}
          </Button>
        )}
      </Box>}
      {authorMode && (
        <>
          <AuthorHandle
            label="Resize tutorial sprite"
            symbol="↘"
            point={spriteResizePoint}
            onPointerDown={resizeSprite}
          />
          <AuthorHandle
            label="Move tutorial dialogue"
            symbol="✥"
            point={dialogueDragPoint}
            onPointerDown={(event) => dragPosition(event, 'dialogue')}
          />
          <AuthorHandle
            label="Move speech tail tip"
            symbol="T"
            point={mouthPoint}
            color="#FF2D95"
            onPointerDown={(event) => dragTailPoint(event, 'tip_offset_px')}
          />
          <AuthorHandle
            label="Move speech tail attachment"
            symbol="B"
            point={tailGeometry.baseCenter}
            color="#FF5F1F"
            onPointerDown={(event) => dragTailPoint(event, 'base_offset_px')}
          />
        </>
      )}
    </Portal>
  );
};

export const TutorialProvider = ({ children }) => {
  const { registerClientExtension } = useClientExtensions();
  const { navigator } = React.useContext(NavigationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [queue, setQueue] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [authorPreview, setAuthorPreview] = useState(null);
  const [pickingTarget, setPickingTarget] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const tutorialActiveRef = useRef(false);
  const minimizedRef = useRef(false);
  const navigationBypassRef = useRef(false);
  const acceptedHistoryIndexRef = useRef(window.history.state?.idx ?? null);
  const restoringHistoryRef = useRef(false);
  const allowedHistoryIndexRef = useRef(null);
  const current = queue[0] || null;
  const displayedCurrent = authorPreview || current;

  tutorialActiveRef.current = Boolean(current);
  minimizedRef.current = minimized;

  const requestNavigation = useCallback((retry) => {
    if (!tutorialActiveRef.current || navigationBypassRef.current) {
      retry();
      return;
    }
    if (!minimizedRef.current) setMinimized(false);
    setPendingNavigation((existing) => existing || { retry, ready: true });
  }, []);

  const navigateFromTutorial = useCallback((route) => {
    navigationBypassRef.current = true;
    try {
      navigate(route);
    } finally {
      navigationBypassRef.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;

    const guardedPush = (...args) => requestNavigation(() => {
      originalPush.apply(navigator, args);
      acceptedHistoryIndexRef.current = window.history.state?.idx ?? acceptedHistoryIndexRef.current;
    });
    const guardedReplace = (...args) => requestNavigation(() => {
      originalReplace.apply(navigator, args);
      acceptedHistoryIndexRef.current = window.history.state?.idx ?? acceptedHistoryIndexRef.current;
    });
    const guardedGo = (...args) => requestNavigation(() => {
      const delta = Number(args[0]);
      if (Number.isInteger(delta) && Number.isInteger(acceptedHistoryIndexRef.current)) {
        allowedHistoryIndexRef.current = acceptedHistoryIndexRef.current + delta;
      }
      originalGo.apply(navigator, args);
    });

    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    navigator.go = guardedGo;

    return () => {
      if (navigator.push === guardedPush) navigator.push = originalPush;
      if (navigator.replace === guardedReplace) navigator.replace = originalReplace;
      if (navigator.go === guardedGo) navigator.go = originalGo;
    };
  }, [navigator, requestNavigation]);

  useEffect(() => {
    const handlePopState = (event) => {
      const nextIndex = event.state?.idx;
      if (restoringHistoryRef.current) {
        restoringHistoryRef.current = false;
        event.stopImmediatePropagation();
        setPendingNavigation((pending) => (
          pending ? { ...pending, ready: true } : pending
        ));
        return;
      }
      if (nextIndex === allowedHistoryIndexRef.current) {
        allowedHistoryIndexRef.current = null;
        acceptedHistoryIndexRef.current = nextIndex;
        return;
      }
      if (!tutorialActiveRef.current || navigationBypassRef.current) {
        acceptedHistoryIndexRef.current = nextIndex ?? acceptedHistoryIndexRef.current;
        return;
      }

      const acceptedIndex = acceptedHistoryIndexRef.current;
      if (!Number.isInteger(nextIndex) || !Number.isInteger(acceptedIndex)) return;
      const delta = nextIndex - acceptedIndex;
      if (!delta) return;

      event.stopImmediatePropagation();
      restoringHistoryRef.current = true;
      window.history.go(-delta);
      setPendingNavigation((existing) => existing || {
        retry: () => {
          allowedHistoryIndexRef.current = nextIndex;
          window.history.go(delta);
        },
        ready: false,
      });
    };

    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, []);

  useEffect(() => {
    if (!current) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [current]);

  useEffect(() => {
    if (!current) return undefined;
    const guardSignOut = (event) => {
      if (!tutorialActiveRef.current || navigationBypassRef.current) return;
      const target = event.target instanceof Element
        ? event.target.closest('#app-top-navigation-sign-out')
        : null;
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      setPendingNavigation((existing) => existing || {
        retry: () => target.click(),
        ready: true,
      });
    };
    document.addEventListener('click', guardSignOut, true);
    return () => document.removeEventListener('click', guardSignOut, true);
  }, [current]);

  const receiveTutorialDelivery = useCallback((delivery, metadata = {}) => {
    const action = normalizeTutorialDelivery(delivery);
    if (!action) {
      console.error('The tutorial extension received an invalid scene payload.');
      return;
    }
    setQueue((existing) => [...existing, {
      action,
      isBatchLast: metadata.isLastForEndpoint !== false,
    }]);
  }, []);

  useEffect(() => (
    registerClientExtension(TUTORIAL_EXTENSION_ENDPOINT, receiveTutorialDelivery)
  ), [receiveTutorialDelivery, registerClientExtension]);

  useEffect(() => {
    if (window.parent === window) return undefined;
    const handleBuilderMessage = (event) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== window.parent ||
        event.data?.source !== TUTORIAL_BUILDER_MESSAGE_SOURCE
      ) return;

      if (event.data.type === 'preview' && [1, 2].includes(event.data.payload?.version)) {
        const action = normalizeTutorialDelivery({
          id: event.data.preview_id || 'tutorial-builder-live-preview',
          extension_key: TUTORIAL_EXTENSION_ENDPOINT,
          event_type: event.data.event_type || 'timer_started',
          context: {},
          payload: event.data.payload,
        });
        if (!action) return;
        setPickingTarget(false);
        setMinimized(false);
        setAuthorPreview({
          action,
          partIndex: Math.min(
            Math.max(0, Number(event.data.part_index) || 0),
            action.payload.parts.length - 1,
          ),
          isAuthorPreview: true,
        });
      }
      if (event.data.type === 'clear-preview') setAuthorPreview(null);
      if (event.data.type === 'pick-target') {
        setAuthorPreview(null);
        setMinimized(false);
        setPickingTarget(event.data.field || 'target');
      }
      if (event.data.type === 'cancel-pick') setPickingTarget(false);
    };
    window.addEventListener('message', handleBuilderMessage);
    return () => window.removeEventListener('message', handleBuilderMessage);
  }, []);

  useEffect(() => {
    if (window.parent === window) return;
    window.parent.postMessage({
      source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
      type: 'ready',
      route: `${location.pathname}${location.search}${location.hash}`,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }, window.location.origin);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (window.parent === window) return undefined;
    const reportViewport = () => window.parent.postMessage({
      source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
      type: 'viewport',
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }, window.location.origin);
    window.addEventListener('resize', reportViewport);
    return () => window.removeEventListener('resize', reportViewport);
  }, []);

  useEffect(() => {
    if (!pickingTarget) return undefined;
    let highlightedElement = null;
    let previousOutline = '';
    let previousOutlineOffset = '';
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';

    const clearHighlight = () => {
      if (!highlightedElement) return;
      highlightedElement.style.outline = previousOutline;
      highlightedElement.style.outlineOffset = previousOutlineOffset;
      highlightedElement = null;
    };
    const selectableElement = (target) => {
      let candidate = target instanceof Element ? target : null;
      while (candidate) {
        const candidateId = candidate.id || '';
        const generatedId = candidateId.startsWith(':') || candidateId.startsWith('mui-');
        if (candidateId && candidateId !== 'root' && !generatedId) return candidate;
        candidate = candidate.parentElement;
      }
      return null;
    };
    const handlePointerMove = (event) => {
      const candidate = selectableElement(event.target);
      if (candidate === highlightedElement) return;
      clearHighlight();
      if (!candidate) return;
      highlightedElement = candidate;
      previousOutline = candidate.style.outline;
      previousOutlineOffset = candidate.style.outlineOffset;
      candidate.style.outline = '4px solid #39FF14';
      candidate.style.outlineOffset = '4px';
    };
    const blockPageInteraction = (event) => {
      suppressTutorialPickerActivation(event);
    };
    const handleClick = (event) => {
      suppressTutorialPickerActivation(event);
      const candidate = selectableElement(event.target);
      if (!candidate) return;
      const targetId = candidate.id;
      const label = (
        candidate.getAttribute('aria-label') ||
        candidate.textContent ||
        candidate.tagName
      ).trim().replace(/\s+/g, ' ').slice(0, 160);
      clearHighlight();
      setPickingTarget(false);
      window.parent.postMessage({
        source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
        type: 'target-picked',
        field: pickingTarget,
        target: targetId,
        label,
        route: `${location.pathname}${location.search}${location.hash}`,
      }, window.location.origin);
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setPickingTarget(false);
      window.parent.postMessage({
        source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
        type: 'pick-cancelled',
      }, window.location.origin);
    };

    const blockedEvents = [
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup',
      'touchstart',
      'touchend',
      'auxclick',
      'dblclick',
      'contextmenu',
    ];
    document.addEventListener('pointermove', handlePointerMove, true);
    blockedEvents.forEach((eventType) => {
      document.addEventListener(eventType, blockPageInteraction, {
        capture: true,
        passive: false,
      });
    });
    document.addEventListener('click', handleClick, true);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearHighlight();
      document.body.style.cursor = previousCursor;
      document.removeEventListener('pointermove', handlePointerMove, true);
      blockedEvents.forEach((eventType) => {
        document.removeEventListener(eventType, blockPageInteraction, true);
      });
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [location.hash, location.pathname, location.search, pickingTarget]);

  const displayedPart = displayedCurrent?.action?.payload?.parts?.[
    displayedCurrent?.partIndex || 0
  ];

  useEffect(() => {
    const route = displayedPart?.navigate_to;
    if (!route) return;
    const destination = new URL(route, window.location.origin);
    if (
      destination.pathname !== location.pathname ||
      destination.search !== location.search ||
      destination.hash !== location.hash
    ) navigateFromTutorial(route);
  }, [
    displayedPart?.navigate_to,
    location.hash,
    location.pathname,
    location.search,
    navigateFromTutorial,
  ]);

  useEffect(() => {
    if (!displayedCurrent || minimized) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMinimized(true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [displayedCurrent, minimized]);

  const handleNext = useCallback(() => {
    if (!current) return;
    const partIndex = current.partIndex || 0;
    if (partIndex < current.action.payload.parts.length - 1) {
      setMinimized(false);
      setQueue((existing) => existing.map((item, index) => (
        index === 0 ? { ...item, partIndex: partIndex + 1 } : item
      )));
      return;
    }
    const rootTaskId = current.action.context?.root_task_id;
    const returnedToTask = Boolean(
      current.isBatchLast &&
      current.action.payload?.next_action === 'return_to_task' &&
      rootTaskId
    );
    if (returnedToTask) {
      navigateFromTutorial(`/task-view?taskId=${encodeURIComponent(rootTaskId)}`);
    }
    setMinimized(false);
    setQueue((existing) => existing.slice(1));
  }, [current, navigateFromTutorial]);

  const handleAuthorNext = useCallback(() => {
    setAuthorPreview((preview) => {
      if (!preview) return preview;
      const nextIndex = Math.min(
        (preview.partIndex || 0) + 1,
        preview.action.payload.parts.length - 1,
      );
      if (nextIndex === preview.partIndex) return preview;
      window.parent.postMessage({
        source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
        type: 'preview-part-selected',
        part_index: nextIndex,
      }, window.location.origin);
      return { ...preview, partIndex: nextIndex };
    });
    setMinimized(false);
  }, []);

  const handleDisplayedNext = authorPreview ? handleAuthorNext : handleNext;

  useEffect(() => {
    if (!displayedCurrent || pickingTarget) return undefined;
    const handleDocumentClick = (event) => {
      const path = event.composedPath?.() || [];
      if (path.some((element) => element?.dataset?.tutorialInteractive)) return;

      const completionControl = event.target instanceof Element
        ? event.target.closest('#task-complete-control')
        : null;
      const assignedTaskId = current?.action?.context?.assigned_task_id;
      if (!authorPreview && completionControl && assignedTaskId) {
        setQueue((existing) => existing.filter(
          (item) => item.action?.context?.assigned_task_id !== assignedTaskId,
        ));
        setMinimized(false);
        return;
      }

      const advanceId = displayedPart?.advance_on_element_id;
      const advanceElement = advanceId ? document.getElementById(advanceId) : null;
      if (advanceElement && event.target instanceof Node && advanceElement.contains(event.target)) {
        handleDisplayedNext();
        return;
      }
      setMinimized(true);
    };
    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [
    authorPreview,
    current?.action?.context?.assigned_task_id,
    displayedCurrent,
    displayedPart?.advance_on_element_id,
    handleDisplayedNext,
    pickingTarget,
  ]);

  const handleStayInTutorial = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const handleEndTutorialAndLeave = useCallback(() => {
    if (!pendingNavigation?.ready) return;
    const retry = pendingNavigation.retry;
    tutorialActiveRef.current = false;
    setQueue([]);
    setMinimized(false);
    setPendingNavigation(null);
    navigationBypassRef.current = true;
    try {
      retry();
    } finally {
      navigationBypassRef.current = false;
    }
  }, [pendingNavigation]);

  const handleAuthorChange = useCallback((part) => {
    setAuthorPreview((preview) => {
      if (!preview) return preview;
      const partIndex = preview.partIndex || 0;
      const parts = preview.action.payload.parts.map((item, index) => (
        index === partIndex ? part : item
      ));
      const payload = { ...preview.action.payload, parts };
      if (window.parent !== window) {
        window.parent.postMessage({
          source: TUTORIAL_PREVIEW_MESSAGE_SOURCE,
          type: 'scene-updated',
          payload,
          part_index: partIndex,
        }, window.location.origin);
      }
      return {
        ...preview,
        action: { ...preview.action, payload },
      };
    });
  }, []);

  return (
    <>
      {children}
      {pickingTarget && (
        <Portal>
          <Box
            role="status"
            sx={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1700,
              pointerEvents: 'none',
              px: 2,
              py: 1.25,
              color: '#090D0E',
              bgcolor: '#39FF14',
              border: '3px solid #090D0E',
              borderRadius: 3,
              boxShadow: '0 5px 0 #137c08',
            }}
          >
            Click an outlined element · Esc cancels
          </Box>
        </Portal>
      )}
      <Dialog
        open={Boolean(pendingNavigation)}
        onClose={handleStayInTutorial}
        data-tutorial-interactive="navigation-confirmation"
        aria-labelledby="tutorial-leave-title"
        aria-describedby="tutorial-leave-description"
        sx={{ zIndex: 1800 }}
      >
        <DialogTitle id="tutorial-leave-title">Leave this tutorial?</DialogTitle>
        <DialogContent>
          <Typography id="tutorial-leave-description">
            Your tutorial task will remain unfinished, so you can continue it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStayInTutorial}>Stay here</Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={!pendingNavigation?.ready}
            onClick={handleEndTutorialAndLeave}
          >
            End tutorial &amp; leave
          </Button>
        </DialogActions>
      </Dialog>
      <TutorialOverlay
        current={displayedCurrent}
        minimized={minimized}
        onMinimize={() => setMinimized(true)}
        onNext={handleDisplayedNext}
        onRestore={() => setMinimized(false)}
        onAuthorChange={authorPreview ? handleAuthorChange : undefined}
      />
    </>
  );
};
